import React from 'react';

import { TFunction } from 'i18next';

import { HIGHLIGHT, HighLightConfigType } from '~/modules/highlight/constants';
import { AiEditOptionType } from '~/modules/tools/components/AiEditSelector';
import { ReactComponent as AudienceIcon } from '~/modules/tools/components/AiEditSelector/assets/audience.svg';
import { ReactComponent as CheckIcon } from '~/modules/tools/components/AiEditSelector/assets/check.svg';
import { ReactComponent as ContinueIcon } from '~/modules/tools/components/AiEditSelector/assets/continue.svg';
import { ReactComponent as DetectIcon } from '~/modules/tools/components/AiEditSelector/assets/detect.svg';
import { ReactComponent as HumanizeIcon } from '~/modules/tools/components/AiEditSelector/assets/humanize.svg';
import { ReactComponent as ImproveIcon } from '~/modules/tools/components/AiEditSelector/assets/improve.svg';
import { ReactComponent as LongerIcon } from '~/modules/tools/components/AiEditSelector/assets/longer.svg';
import { ReactComponent as ShorterIcon } from '~/modules/tools/components/AiEditSelector/assets/shorter.svg';
import { ReactComponent as SummarizeIcon } from '~/modules/tools/components/AiEditSelector/assets/summarize.svg';
import { ReactComponent as ToneIcon } from '~/modules/tools/components/AiEditSelector/assets/tone.svg';
import { ReactComponent as TranslateIcon } from '~/modules/tools/components/AiEditSelector/assets/translate.svg';
import analytics from '~/services/analytics';

import SelectModeText from './SelectModeText';

export const IsShowedAiHumanizerTutorial = 'is_showed_ai_humanizer_tutorial_v2';

export const AiHumanizerTitle = 'AI Text Humanizer';

export const HUMANIZER_MAX_FILE_SIZE = 20000000; //20MB
export const HUMANIZER_VALID_FILE_EXTENSIONS = ['docx', 'pdf', 'txt'];

export enum HUMANIZER_MODE_V2 {
  Human = 'sound_human',
  Auto = 'auto',
  Detectors = 'bypass_detectors',
}

export const getHumanizerModeV4Options = (
  t: TFunction,
): Record<
  HUMANIZER_MODE_V2,
  {
    value: string;
    text: string;
    tip: string;
  }
> => ({
  [HUMANIZER_MODE_V2.Human]: {
    value: 'sound_human',
    text: t('tools:ai_humanizer_v2.form_mode_options_text[0]', 'Sound Human'),
    tip: t(
      'highlight:ai_humanizer.sound_human.text',
      'Light humanization mode aimed to make text appear as human written but not necessarily pass the strictest detectors',
    ),
  },
  [HUMANIZER_MODE_V2.Auto]: {
    value: 'auto',
    text: t('tools:ai_humanizer_v2.form_mode_options_text[1]', 'Auto'),
    tip: t(
      'highlight:ai_humanizer.goal_auto.text',
      'The system detects the best mode for a text to achieve a balanced result',
    ),
  },
  [HUMANIZER_MODE_V2.Detectors]: {
    value: 'bypass_detectors',
    text: t(
      'tools:ai_humanizer_v2.form_mode_options_text[2]',
      'Bypass Detectors',
    ),
    tip: t(
      'highlight:ai_humanizer.bypass_detector.text',
      'Aggressive setting with the primary goal of bypassing the strictest detectors. Caution: sometimes, this may bring unusual results or grammar mistakes, so double-checking the text is recommended.',
    ),
  },
});

export const CLICK_TOGGLER_ANALYTIC_NAMES: Record<HUMANIZER_MODE_V2, string> = {
  [HUMANIZER_MODE_V2.Auto]: 'ai humanizer - select auto goal',
  [HUMANIZER_MODE_V2.Human]: 'ai humanizer - select sound human goal',
  [HUMANIZER_MODE_V2.Detectors]: 'ai humanizer - select bypass detectors goal',
};

// don't translate values for Humanizer, Detector and Plagiarism
export const getHumanizerAiEditOptions = (t: TFunction): AiEditOptionType[] => [
  {
    text: t('general:humanize', 'Humanize'),
    value: 'humanize',
    icon: <HumanizeIcon />,
  },
  {
    text: t('general:improve', 'Improve'),
    value: t('ai_edit_options:values[0]', 'improve this text'),
    icon: <ImproveIcon />,
  },
  {
    text: t('general:shorter', 'Shorter'),
    value: t('ai_edit_options:values[2]', 'make this text shorter'),
    icon: <ShorterIcon />,
  },
  {
    text: t('general:longer', 'Longer'),
    value: t('ai_edit_options:values[4]', 'make this text longer'),
    icon: <LongerIcon />,
  },
  {
    text: t('general:change_tone', 'Change Tone'),
    value: t(
      'ai_edit_options:values[8]',
      'change tone of voice of this text to [paste new tone]',
    ),
    icon: <ToneIcon />,
  },
  {
    text: t('general:detect_ai', 'Detect Ai'),
    value: 'detect ai',
    icon: <DetectIcon />,
  },
  {
    text: t('general:check_plagiarism', 'Check Plagiarism'),
    value: 'check plagiarism',
    icon: <CheckIcon />,
  },
  {
    text: t('general:summarize', 'Summarize'),
    value: t('ai_edit_options:values[6]', 'summarize this text'),
    icon: <SummarizeIcon />,
  },
  {
    text: t('general:change_audience', 'Change Audience'),
    value: t(
      'ai_edit_options:values[10]',
      'rewrite text for the next audience [paste new audience]',
    ),
    icon: <AudienceIcon />,
  },
  {
    text: t('general:continue_writing', 'Continue Writing'),
    value: t('ai_edit_options:values[1]', 'continue writing this text'),
    icon: <ContinueIcon />,
  },
  {
    text: t('general:explain', 'Explain'),
    value: t('ai_edit_options:values[9]', 'explain this text'),
    icon: <ImproveIcon />,
  },
  {
    text: t('general:translate', 'Translate'),
    value: t(
      'ai_edit_options:values[11]',
      'translate this text to: [output language]',
    ),
    icon: <TranslateIcon />,
  },
];

export const getAiHumanizerHighlightConfig = (
  t: TFunction,
): Record<
  | HIGHLIGHT.AI_HUMANIZER_MODE_SELECT
  | HIGHLIGHT.AI_HUMANIZER_DETECT_AI_V2
  | HIGHLIGHT.AI_HUMANIZER_AI_EDIT_V2,
  HighLightConfigType
> => {
  return {
    [HIGHLIGHT.AI_HUMANIZER_DETECT_AI_V2]: {
      text: t(
        'highlight:ai_humanizer.detect_ai.text',
        'Scan the text to double-check if it is being flagged as AI-generated',
      ),
      placement: 'top',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiHumanizerTitle,
          tip_name: HIGHLIGHT.AI_HUMANIZER_DETECT_AI_V2,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiHumanizerTitle,
          tip_name: HIGHLIGHT.AI_HUMANIZER_DETECT_AI_V2,
        });
      },
    },
    [HIGHLIGHT.AI_HUMANIZER_MODE_SELECT]: {
      title: t(
        'highlight:ai_humanizer.ai_select_mode.title',
        'Humanization Mode Setting',
      ),
      text: <SelectModeText />,
      placement: 'top',
      buttonText: t('general:got_it', 'Got it'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiHumanizerTitle,
          tip_name: HIGHLIGHT.AI_HUMANIZER_MODE_SELECT,
        });
      },
    },
    [HIGHLIGHT.AI_HUMANIZER_AI_EDIT_V2]: {
      title: t('highlight:ai_humanizer.ai_edit.title', 'AI Edit'),
      text: t(
        'humanizer.ai_edit.text',
        'This feature allows you to ask AI to do different things with text, such as summarize, change tone, or do anything else you ask.',
      ),
      placement: 'top',
      buttonText: t('general:finish', 'Finish'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiHumanizerTitle,
          tip_name: HIGHLIGHT.AI_HUMANIZER_AI_EDIT_V2,
        });
      },
      onSubmitCallback: (updateUserExtraFields): void => {
        updateUserExtraFields({
          highlights: { [IsShowedAiHumanizerTutorial]: true },
        });
        analytics.trackEvent('tutorial - finished', {
          tool: AiHumanizerTitle,
          tip_name: HIGHLIGHT.AI_HUMANIZER_AI_EDIT_V2,
        });
      },
    },
  };
};
